import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Brain } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/app";
  const [form, setForm] = useState({ name: "", workspace_name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await register(form);
      toast.success("Workspace created");
      nav(next);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-10 border-r border-border/60 relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-20 pointer-events-none" />
        <Link to="/" className="relative flex items-center gap-2 font-display font-semibold">
          <Brain className="w-5 h-5" /> RAGNES
        </Link>
        <div className="relative">
          <p className="eyebrow mb-4">Set up your brain</p>
          <p className="font-display text-3xl leading-tight max-w-md">
            One workspace. Every process. Instantly queryable.
          </p>
        </div>
        <p className="relative text-xs text-muted-foreground font-mono">bootstrap.workspace</p>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="register-form">
          <div>
            <p className="eyebrow mb-3">Create</p>
            <h1 className="font-display text-3xl">New workspace</h1>
          </div>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Your name</Label>
              <Input id="name" required value={form.name} onChange={set("name")} data-testid="register-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ws">Workspace / company name</Label>
              <Input id="ws" required value={form.workspace_name} onChange={set("workspace_name")} data-testid="register-workspace-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="register-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="register-password-input" />
            </div>
            {err && <p className="text-sm text-destructive" data-testid="register-error">{err}</p>}
            <Button type="submit" disabled={busy} data-testid="register-submit-btn" className="w-full rounded-full h-11">
              {busy ? "Creating…" : "Create workspace"}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Have an account?{" "}
            <Link to="/login" className="text-foreground underline underline-offset-4" data-testid="register-login-link">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
